// src/store/index.js
import { createStore } from 'vuex'
import { supabase } from '@/supabaseClient'

export default createStore({
  state: {
    user: null,        // your public.users row (or auth user fallback)
    session: null,     // supabase session
    _authUnsub: null,  // unsubscribe fn for auth listener
  },

  getters: {
    isLoggedIn: (state) => !!state.user,
  },

  mutations: {
    setUser(state, user) { state.user = user },
    setSession(state, session) { state.session = session },
    setAuthUnsub(state, fn) { state._authUnsub = fn },
    clearAuthUnsub(state) { state._authUnsub = null },
  },

  actions: {
    // Call this ONCE before mount (see main.js)
    async initAuth({ state, commit, dispatch }) {
      if (state._authUnsub) return; // avoid duplicates (HMR / re-mounts)

      // 1) hydrate store immediately
      const { data: { session } = {} } = await supabase.auth.getSession()
      commit('setSession', session || null)
      if (session) {
        await dispatch('fetchUserDataFromSession', session)
      } else {
        commit('setUser', null)
      }

      // 2) subscribe to future auth changes (single subscription)
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (_event, newSession) => {
          commit('setSession', newSession || null)
          if (newSession) {
            await dispatch('fetchUserDataFromSession', newSession)
          } else {
            commit('setUser', null)
          }
        }
      )
      commit('setAuthUnsub', () => subscription.unsubscribe())
    },

    // Convenience if you call it elsewhere
    async fetchUserData({ commit, dispatch }) {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !sessionData?.session) {
        console.error('Error fetching session:', sessionError)
        commit('setUser', null)
        commit('setSession', null)
        return
      }
      const session = sessionData.session
      commit('setSession', session)
      await dispatch('fetchUserDataFromSession', session)
    },

    // Loads your row from public.users by auth uid; falls back to auth user
    async fetchUserDataFromSession({ commit }, session) {
      const uid = session?.user?.id
      if (!uid) { commit('setUser', null); return }

      const { data: userProfile, error } = await supabase
        .from('users')
        .select('*')
        .eq('uid', uid)       // ensure your column is named 'uid'
        .single()

      if (error) {
        console.warn('Profile missing; falling back to auth user:', error.message)
        commit('setUser', session.user)
      } else {
        // normalize keys if your app reads camelCase
        const normalized = {
          ...userProfile,
          isAdmin: userProfile.is_admin ?? userProfile.isAdmin ?? false,
          is_agent: userProfile.is_agent ?? false,
        }
        commit('setUser', normalized)
      }
    },

    async signOut({ commit }) {
      try {
        await supabase.auth.signOut()
      } catch (e) {
        if (!String(e?.message || '').includes('Auth session missing')) {
          console.error('signOut error:', e)
        }
      } finally {
        commit('setUser', null)
        commit('setSession', null)
      }
    }
  },
})
